"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HttpService = void 0;
const http_errors_1 = require("./http-errors");
const api_entity_response_1 = require("./transform/api-entity.response");
const api_entity_list_response_1 = require("./transform/api-entity-list.response");
class HttpService {
    baseUrl;
    constructor(baseUrl) {
        this.baseUrl = baseUrl;
    }
    async handleApiError(response) {
        if (response.ok) {
            return;
        }
        const _data = await response.json().catch((_) => {
            return null;
        });
        const status = response.status || _data.statusCode;
        if (status === 401) {
            return new http_errors_1.UnauthorizedError(_data);
        }
        else if (status === 400) {
            return new http_errors_1.BadRequestError(_data);
        }
        else if (status === 403) {
            return new http_errors_1.ForbiddenError(_data);
        }
        return new http_errors_1.UnExpectedError(_data);
    }
    async parseApiResponse(response, opts) {
        const error = await this.handleApiError(response);
        if (error) {
            throw error;
        }
        if (opts?.transformTo === 'blob') {
            const data = await response.blob();
            return {
                response,
                data: data,
            };
        }
        const _data = (await response.json());
        try {
            return {
                response,
                data: opts?.schema ? opts.schema.parse(_data) : _data,
            };
        }
        catch (err) {
            console.error('parseApiResponse', {
                response,
                transform: _data,
            });
            throw err;
        }
    }
    getApiUrlOf(_path, _params) {
        const path = _path.startsWith('/') ? _path.slice(1) : _path;
        const params = _params
            ? Object.keys(_params).reduce((acc, key) => {
                if (typeof _params[key] !== 'undefined') {
                    acc[key] = _params[key];
                }
                return acc;
            }, {})
            : undefined;
        const url = new URL(this.baseUrl);
        url.pathname = path ? `${url.pathname}/${path}` : url.pathname;
        url.search = new URLSearchParams(params).toString();
        return url;
    }
    get(path, opts) {
        const url = this.getApiUrlOf(path, opts?.params);
        return this.fetch(url, {
            method: 'GET',
            schema: opts?.schema,
        }).then((apiResponse) => apiResponse.data);
    }
    getEntity(path, opts) {
        return this.get(path, opts).then((response) => {
            const transform = new api_entity_response_1.TransformEntityApiResponse(response);
            return {
                transform,
                response,
            };
        });
    }
    getEntityList(path, opts) {
        return this.get(path, opts).then((response) => {
            const transform = new api_entity_list_response_1.TransformEntityListApiResponse(response);
            return {
                transform,
                response,
            };
        });
    }
    post(path, opts) {
        const url = this.getApiUrlOf(path);
        return this.fetch(url, {
            method: 'POST',
            ...opts,
        }).then((apiResponse) => apiResponse.data);
    }
    postEntity(path, opts) {
        return this.post(path, opts).then((response) => {
            const transform = new api_entity_response_1.TransformEntityApiResponse(response);
            return {
                transform,
                response,
            };
        });
    }
    postEntityList(path, opts) {
        return this.post(path, opts).then((response) => {
            const transform = new api_entity_list_response_1.TransformEntityListApiResponse(response);
            return {
                transform,
                response,
            };
        });
    }
    put(path, options) {
        const url = this.getApiUrlOf(path);
        return this.fetch(url, {
            method: 'PUT',
            ...options,
        }).then((apiResponse) => apiResponse.data);
    }
    putEntity(path, opts) {
        return this.put(path, opts).then((response) => {
            const transform = new api_entity_response_1.TransformEntityApiResponse(response);
            return {
                transform,
                response,
            };
        });
    }
    delete(path, opts) {
        const url = this.getApiUrlOf(path);
        return this.fetch(url, {
            method: 'DELETE',
            body: opts?.body,
            schema: opts?.schema,
        }).then((apiResponse) => apiResponse.data);
    }
    fetch(url, { schema, body: _body, transformTo = 'json', ...options }) {
        const isMultipart = options.headers?.['Content-Type'] === 'multipart/form-data';
        let body;
        if (_body) {
            if (_body instanceof FormData) {
                body = _body;
            }
            else if (isMultipart) {
                const fomData = new FormData();
                for (const key of Object.keys(_body)) {
                    const value = _body[key];
                    if (value instanceof File) {
                        fomData.append(key, value);
                    }
                    else if (value instanceof Date) {
                        fomData.append(key, value.toISOString());
                    }
                    else if (typeof value === 'object' && value) {
                        fomData.append(key, JSON.stringify(value));
                    }
                    else {
                        fomData.append(key, value);
                    }
                }
                body = fomData;
            }
            else {
                body = JSON.stringify(_body);
            }
        }
        // Content-Type is auto set to:
        // - multipart/form-data + boundary: if body instanceof FormData
        // - application/x-www-form-urlencoded: if body instance of URLSearchParams
        if (isMultipart) {
            delete options.headers?.['Content-Type'];
        }
        else if (body) {
            options.headers = {
                'Content-Type': 'application/json',
                ...options.headers,
            };
        }
        return fetch(url, {
            ...options,
            credentials: 'include',
            body,
        }).then((res) => this.parseApiResponse(res, { transformTo, schema }));
    }
}
exports.HttpService = HttpService;
